import type { Event } from '@sentry/nextjs'
import { isAxiosError } from 'axios'

type Correlation = { traceId: string; project: string }
// Only needed when a caller replaces the Axios error with a domain error.
const wrappedErrors = new WeakMap<object, Correlation>()
const projects: Record<string, string> = {
  'https://api.zoe.com': 'backend-producti-b8633498',
  'https://api.joinzoe.com': 'backend-producti-b8633498',
  'https://public.prod.zoe.net': 'backend-producti-b8633498',
  'https://apik.stage.zoe.net': 'backend-stage-7693ab8c',
  'https://public.stage.zoe.net': 'backend-stage-7693ab8c',
}

const getCorrelation = (error: unknown): Correlation | undefined => {
  if (typeof error !== 'object' || error === null) return
  if (wrappedErrors.has(error)) return wrappedErrors.get(error)
  if (!isAxiosError(error)) return
  const header: unknown = error.response?.headers['x-trace-id']
  if (typeof header !== 'string') return
  const traceId = /^([0-9a-f]{32})(?:\/[0-9a-f]{1,20};o=[01])?$/i.exec(header)?.[1]?.toLowerCase()
  if (!traceId || traceId === '0'.repeat(32)) return
  try {
    const project = projects[new URL(error.config?.url ?? '', error.config?.baseURL).origin]
    if (project) return { traceId, project }
  } catch {
    // Relative/local service URLs cannot identify a GCP project.
  }
  return undefined
}

/** Keep only response correlation, not the original error's payload or credentials. */
export const copyRequestCorrelation = <T extends Error>(target: T, source: unknown): T => {
  const correlation = getCorrelation(source)
  if (correlation) wrappedErrors.set(target, correlation)
  return target
}

export const enrichBackendRequestEvent = (event: Event, error: unknown): Event => {
  const correlation = getCorrelation(error)
  if (!correlation) return event
  const { traceId, project } = correlation
  event.tags = { ...event.tags, 'backend.trace_id': traceId }
  // Preserve the frontend trace context; correlate using the actual failed response.
  event.contexts = {
    ...event.contexts,
    backend_request: { trace_id: traceId },
    gcp: { trace: `https://console.cloud.google.com/traces/explorer;traceId=${traceId}?project=${project}` },
  }
  return event
}
